import { trpc } from '@/config/trpc'
import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router'
import { usePostHog } from 'posthog-js/react'
import { useBrand } from '@/context/brandContext'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { GradientAnimation } from '@/components/ui/GradientAnimation'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { CompanySetupUrlStep } from '@/components/company-setup/company-setup-url-step'
import { CompanySetupLoadingStep } from '@/components/company-setup/company-setup-loading-step'
import { LANGUAGE_SUGGESTIONS } from '@/lib/onboarding/languages'
import {
  applyOnboardingWebsite,
  buildStarterTemplate,
  onboardingWebsiteUrl,
} from '@/lib/onboarding/starterTemplate'
import { toast } from 'sonner'
import { useOnboardingStep } from '@/lib/onboarding/tracking'
import { captureError } from '@/lib/errors'

// Onboarding entry. The brand-analysis wizard (logos/colors/summary/voice steps,
// persona creation) has been replaced by the moodboard flow. We ask for the
// post language FIRST — it's set on the brand before anything is generated, so
// the moodboard brief, topic suggestions, and posts all come out in it — then a
// website (optional), seed a starter moodboard, and drop the user into the
// canvas in onboarding mode. See docs/design/onboarding-moodboard.md.
//
// Resumable, not re-creatable: an un-onboarded brand has exactly one in-progress
// moodboard. Re-entering this page resumes that board instead of duplicating it.
// The one exception is `?edit=1`, the canvas's explicit "Back": it shows the steps
// again, prefilled, and saving updates that same board instead of creating one.
type EntryStep = 'language' | 'website'

export function CompanySetupPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const posthog = usePostHog()
  const { currentBrand } = useBrand()
  const {
    data: moodboards,
    isLoading: moodboardsLoading,
    isFetching: moodboardsFetching,
    isError: moodboardsError,
    refetch: refetchMoodboards,
  } = trpc.moodboard.getAll.useQuery()
  const utils = trpc.useUtils()

  // The in-progress board the user came back from, when editing. Same pick as the
  // resume redirect below.
  const resumeBoard = moodboards?.length
    ? [...moodboards].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      )[0]!
    : null
  const editBoard = searchParams.get('edit') === '1' ? resumeBoard : null

  const [step, setStep] = useState<EntryStep>('language')
  const [language, setLanguage] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  // True only when this page actually renders a step. The page also acts as a
  // router (resume an in-progress board, bounce onboarded brands to /), and the
  // Layout gate sends every blocked path here, so firing on mount over-counted
  // starts with users who never saw a step.
  const showingSteps =
    !moodboardsError &&
    !!currentBrand &&
    !moodboardsLoading &&
    !moodboardsFetching &&
    !!moodboards &&
    currentBrand.onboardedAt == null &&
    moodboards.length === 0

  const startedRef = useRef(false)
  useEffect(() => {
    if (!showingSteps || startedRef.current) return
    startedRef.current = true
    posthog.capture('onboarding_started')
  }, [showingSteps, posthog])

  // Editing an existing board is not a fresh pass through the funnel; the canvas
  // tracks the way back on its own.
  const languageStep = useOnboardingStep('language', showingSteps && step === 'language')
  const websiteStep = useOnboardingStep('website', showingSteps && step === 'website')
  const boardPath = (id: number) => `/moodboards/${id}?onboarding=1`

  // Seed the picker from any language already chosen (e.g. dropped out after the
  // language step but before creating a moodboard).
  useEffect(() => {
    if (currentBrand?.language) setLanguage(currentBrand.language)
  }, [currentBrand?.language])

  const { mutateAsync: updateBrand } = trpc.brand.update.useMutation()
  const { mutateAsync: createMoodboard } = trpc.moodboard.create.useMutation()
  const { mutateAsync: updateMoodboard } = trpc.moodboard.update.useMutation()
  const { mutateAsync: cleanupAssets } = trpc.moodboard.cleanupAssets.useMutation()

  // Persist the chosen language on the brand, then advance to the website step.
  // Best-effort: a failure shouldn't trap the user — they continue, language just
  // falls back to topic-inferred.
  const submitLanguage = async (chosen: string) => {
    const lang = chosen.trim()
    if (lang) {
      try {
        await updateBrand({ language: lang })
        await utils.brand.getCurrent.invalidate()
      } catch (error) {
        console.warn('Failed to save brand language during onboarding:', error)
      }
    }
    posthog.capture('onboarding_language_set', { has_language: !!lang })
    languageStep.complete({ language: lang || null })
    setStep('website')
  }

  // Create the onboarding moodboard, seed it, and enter the canvas. `website`
  // is undefined when the user skipped the website prompt.
  const startMoodboard = async (website?: string) => {
    if (editBoard) return saveWebsite(editBoard, website ?? null)
    if (isCreating) return
    setIsCreating(true)
    websiteStep.complete({ had_website: !!website, skipped: !website })
    try {
      const name = currentBrand?.name ? `${currentBrand.name} moodboard` : 'My moodboard'
      const result = await createMoodboard({
        name,
        items: buildStarterTemplate(website),
      })
      // Practically unreachable during onboarding (a new brand means a fresh
      // free user with zero boards, or a paid tier) — but fail loudly if hit.
      if (!result.ok) throw new Error('Moodboard limit reached')
      const created = result.moodboard
      // Prime the getAll cache so an immediate browser-back resumes this board
      // instead of seeing the stale empty list and offering to create another.
      utils.moodboard.getAll.setData(undefined, (prev) => [...(prev ?? []), created])
      posthog.capture('onboarding_moodboard_created', {
        moodboard_id: created.id,
        had_website: !!website,
      })
      // `onboarding=1` puts the canvas in onboarding mode (tutorial + Finalize CTA).
      navigate(`/moodboards/${created.id}?onboarding=1`)
    } catch (error) {
      websiteStep.track('onboarding_moodboard_create_failed', {
        message: error instanceof Error ? error.message : String(error),
      })
      captureError(error, { area: 'onboarding', action: 'moodboard_create' })
      toast.error('Something went wrong setting up your moodboard. Please try again.')
      setIsCreating(false)
    }
  }

  // Edit path: point the existing board at the (possibly new) website and go back to
  // it. An unchanged website changes nothing.
  const saveWebsite = async (board: NonNullable<typeof editBoard>, website: string | null) => {
    if (isCreating) return
    if (website === onboardingWebsiteUrl(board.items)) {
      navigate(boardPath(board.id), { replace: true })
      return
    }
    setIsCreating(true)
    try {
      const items = applyOnboardingWebsite(board.items, website)
      const updated = await updateMoodboard({ id: board.id, items })
      if (updated) {
        utils.moodboard.getById.setData({ id: board.id }, updated)
        utils.moodboard.getAll.setData(undefined, (prev) =>
          prev?.map((m) => (m.id === updated.id ? updated : m)),
        )
      }
      // The old site's logo file is no longer on the board.
      const kept = new Set(items.flatMap((i) => (i.type === 'file' ? [i.file.key] : [])))
      const dropped = board.items.flatMap((i) =>
        i.type === 'file' && !kept.has(i.file.key) ? [i.file.key] : [],
      )
      if (dropped.length > 0) void cleanupAssets({ id: board.id, keys: dropped }).catch(() => {})
      posthog.capture('onboarding_website_changed', {
        moodboard_id: board.id,
        had_website: !!onboardingWebsiteUrl(board.items),
        has_website: !!website,
      })
      navigate(boardPath(board.id), { replace: true })
    } catch (error) {
      captureError(error, { area: 'onboarding', action: 'moodboard_website_change' })
      toast.error('Something went wrong updating your moodboard. Please try again.')
      setIsCreating(false)
    }
  }

  // A failed list would otherwise fall into the loading branch below and park the
  // user on an animation that never resolves.
  if (moodboardsError) {
    return (
      <section className="relative min-h-screen">
        <GradientAnimation animationDuration="25s" />
        <div className="mx-auto flex min-h-screen max-w-[1200px] flex-col items-center justify-center gap-3 p-6 text-center">
          <h1 className="text-foreground text-lg font-medium">We couldn't start your setup</h1>
          <p className="text-muted-foreground max-w-sm text-sm">
            Something went wrong loading your account. Try again in a moment.
          </p>
          <Button variant="outline" onClick={() => refetchMoodboards()}>
            Try again
          </Button>
        </div>
      </section>
    )
  }

  // Wait for brand + moodboards before deciding. Avoids flashing a step (and
  // risking a duplicate create) before we know whether one already exists.
  // `isFetching` matters as much as `isLoading` here: a cached list from a brand
  // we just left still reads as loaded, and resuming onto one of its boards sends
  // the user to a moodboard id that 404s under the new brand.
  if (!currentBrand || moodboardsLoading || moodboardsFetching || !moodboards) {
    return (
      <section className="relative min-h-screen">
        <GradientAnimation animationDuration="25s" />
        <div className="mx-auto h-full max-w-[1200px]">
          <CompanySetupLoadingStep />
        </div>
      </section>
    )
  }

  // Already onboarded — nothing to do here.
  if (currentBrand.onboardedAt != null) {
    return <Navigate to="/" replace />
  }

  // Resume an in-progress onboarding board (most recently touched) instead of
  // creating another.
  if (resumeBoard && !editBoard) {
    return <Navigate to={boardPath(resumeBoard.id)} replace />
  }

  return (
    <section className="relative min-h-screen">
      <GradientAnimation animationDuration="25s" />
      <div className="mx-auto h-full max-w-[1200px]">
        {isCreating ? (
          <CompanySetupLoadingStep />
        ) : step === 'language' ? (
          <LanguageStep
            value={language}
            onChange={setLanguage}
            onContinue={() => submitLanguage(language)}
            onBack={
              editBoard ? () => navigate(boardPath(editBoard.id), { replace: true }) : undefined
            }
          />
        ) : (
          <CompanySetupUrlStep
            onNext={(url) => startMoodboard(url)}
            onSkip={() => startMoodboard()}
            onBack={() => setStep('language')}
            initialUrl={editBoard ? (onboardingWebsiteUrl(editBoard.items) ?? '') : undefined}
          />
        )}
      </div>
    </section>
  )
}

function LanguageStep({
  value,
  onChange,
  onContinue,
  onBack,
}: {
  value: string
  onChange: (v: string) => void
  onContinue: () => void
  // Only when editing an existing board: return to it without changes.
  onBack?: () => void
}) {
  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center px-6 py-16">
      <div className="animate-fade-up w-full max-w-[560px]">
        {onBack && <SetupBackButton onClick={onBack} label="Back to your moodboard" />}
        <p className="text-primary mb-4 text-sm font-semibold uppercase tracking-[0.2em]">
          Welcome to CASSI
        </p>
        <h1 className="text-foreground text-4xl font-bold leading-[1.1] tracking-tight lg:text-5xl">
          What language should CASSI write your posts in?
        </h1>
        <p className="text-foreground/70 mt-4 text-lg leading-relaxed">
          CASSI creates social media posts for you. Pick the language it should write them in. You
          can change this later.
        </p>

        <div className="mt-10 flex flex-wrap gap-2.5">
          {LANGUAGE_SUGGESTIONS.map((l) => (
            <button
              key={l}
              onClick={() => onChange(l)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                value.trim() === l
                  ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                  : 'border-border bg-card/40 text-foreground hover:border-primary/50 hover:bg-card/70'
              }`}>
              {l}
            </button>
          ))}
        </div>

        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Or type a language…"
          className="mt-4 h-12 text-base"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && value.trim()) onContinue()
          }}
        />

        <Button onClick={onContinue} size="lg" className="mt-8 gap-2" disabled={!value.trim()}>
          Continue
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  )
}

function SetupBackButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className="text-foreground/70 hover:text-foreground mb-8 flex items-center gap-1.5 text-sm font-medium transition-colors">
      <ArrowLeft size={16} />
      {label}
    </button>
  )
}
